import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedVehicleTypeId } from '#api/enhancements/enhancedTmsApiTypes'
import { useVehicleTypeIdsQuery } from '#api/tmsApiHooks'
import { Icon } from '#components/Icon'
import { Select, type SelectEntry } from '#components/Select'
import { Spinner } from '#components/Spinner'
import { SelectionField } from './SelectionField'
import { TextInput } from './TextInput'

export const VehicleTypeInput: FC<{
  vehicleTypeId?: EnhancedVehicleTypeId
  onSelected: (vehicleTypeId: EnhancedVehicleTypeId) => void
  kind?: 'inline' | 'full'
  disabled?: boolean
}> = ({ vehicleTypeId, onSelected, kind, disabled }) => {
  const { t } = useTranslation()
  const vehicleTypeIds = useVehicleTypeIdsQuery()

  const input = useMemo(() => {
    if (vehicleTypeIds.isLoading) {
      return (
        <section className="split-flex">
          <Spinner size="s" />
          <span>{t('setup.vehicle-type.loading', 'Loading vehicle types…')}</span>
        </section>
      )
    }
    if (!vehicleTypeIds.isSuccess) {
      return (
        <section className="has-error">
          <Icon iconUrl={undefined} />
          <span>{t('setup.vehicle-type.error.failed-loading-vehicle-types', 'Failed loading vehicle types')}</span>
        </section>
      )
    }

    const vehicleTypeSelectItems = (vehicleTypeIds.data?.listOfVehicleTypes ?? [])
      .map(
        (v) =>
          ({
            id: v.vehicleTypeID ?? ('unknown' as unknown as EnhancedVehicleTypeId),
            value: `${v.vehicleTypeName ?? t('setup.vehicle-type.unknown')} (${v.vehicleTypeID})`,
          }) satisfies SelectEntry<EnhancedVehicleTypeId, string>,
      )
      .sort((a, b) => a.value.localeCompare(b.value))

    return (
      <Select
        required
        slim
        disabled={disabled}
        label={kind === 'inline' ? undefined : t('setup.vehicle-type.label', 'Vehicle Type')}
        options={vehicleTypeSelectItems}
        value={vehicleTypeId}
        placeholder={t('setup.vehicle-type.unknown', 'Unknown Vehicle Type')}
        onChange={(id) => {
          if (id) {
            onSelected(id)
          }
        }}
      />
    )
  }, [t, vehicleTypeId, onSelected, vehicleTypeIds, kind, disabled])

  return (
    <SelectionField>
      {input}
      <TextInput
        required
        type="number"
        value={vehicleTypeId}
        disabled={disabled}
        onChange={(x) => {
          if (!Number.isNaN(x.target.valueAsNumber)) {
            onSelected(x.target.valueAsNumber as EnhancedVehicleTypeId)
          }
        }}
      />
    </SelectionField>
  )
}
